import { useEffect, useRef, useState, type FormEvent } from "react";
import { apiRequest, signInWithUsername } from "../api";
import {
  createCheemsPayOrder,
  isFirstPurchaseAccount,
  requiresRefundPolicyConsent,
  PENDING_STOREFRONT_PRODUCT_KEY,
  rememberPendingStorefrontProduct,
  storefrontReturnPath,
  takePendingStorefrontProduct,
} from "../checkout";
import { AsyncStatus, Button, Dialog, Field, Shell, TopbarAccountLink } from "../components";
import { BomanaStorefront } from "../components/BomanaStorefront";
import { PasswordField } from "../components/PasswordField";
import { RecoveryEmailCheckoutDialog } from "../components/RecoveryEmailCheckoutDialog";
import { setPageMetadata } from "../page-metadata";
import {
  isTeamNickname,
  parsePendingTeamInviteJoin,
  serializePendingTeamInviteJoin,
  teamInvitePendingKey,
  teamInviteTokenFromReturnPath,
} from "../team-invite-pending";
import type { AccountResponse, Offer, SessionResponse } from "../types";

type Mode = "login" | "register";
type TwoFactorMethod = "totp" | "backup";

const HOMEPAGE_SALES_PAUSED = true;

interface RegistrationResponse {
  warning?: string;
  referral?: { applied: boolean };
}

export function requestedAccessMode(search: string): Mode | null {
  const access = new URLSearchParams(search).get("access");
  return access === "login" || access === "register" ? access : null;
}

export function bindPendingTeamInviteToAuthenticatedUser(
  returnPath: string,
  userId: string,
  storage: Pick<Storage, "getItem" | "setItem">,
): boolean {
  const token = teamInviteTokenFromReturnPath(returnPath);
  if (!token) return false;
  const key = teamInvitePendingKey(token);
  const pending = parsePendingTeamInviteJoin(storage.getItem(key), isTeamNickname);
  if (!pending) return false;
  storage.setItem(
    key,
    serializePendingTeamInviteJoin(pending.nickname, pending.idempotencyKey, userId),
  );
  return true;
}

export function hasPendingTeamInviteToBind(
  returnPath: string,
  storage: Pick<Storage, "getItem">,
): boolean {
  const token = teamInviteTokenFromReturnPath(returnPath);
  return Boolean(
    token &&
      parsePendingTeamInviteJoin(
        storage.getItem(teamInvitePendingKey(token)),
        isTeamNickname,
      ),
  );
}

export function AuthPage() {
  const requestedMode = requestedAccessMode(window.location.search);
  const [mode, setMode] = useState<Mode>(requestedMode ?? "login");
  const [offers, setOffers] = useState<Offer[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [signedIn, setSignedIn] = useState(false);
  const [accessOpen, setAccessOpen] = useState(
    requestedMode !== null ||
      Boolean(new URLSearchParams(window.location.search).get("returnTo")),
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] =
    useState<TwoFactorMethod>("totp");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [recoverySecret, setRecoverySecret] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralSource, setReferralSource] = useState<"LINK" | "CODE">(
    "CODE",
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState<AccountResponse["account"] | null>(
    null,
  );
  const [acceptedRefundPolicies, setAcceptedRefundPolicies] = useState<
    Record<string, boolean>
  >({});
  const [busyProduct, setBusyProduct] = useState("");
  const [checkoutProductId, setCheckoutProductId] = useState("");
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [checkoutReferralCode, setCheckoutReferralCode] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const catalogRequestVersion = useRef(0);
  const pendingPurchaseHandled = useRef(false);
  const mountedRef = useRef(true);

  const loadCatalog = async () => {
    const requestVersion = ++catalogRequestVersion.current;
    setCatalogLoaded(false);
    setCatalogError("");
    try {
      const catalog = await apiRequest<{ offers: Offer[] }>(
        "/api/catalog?appId=bomana",
      );
      if (
        mountedRef.current &&
        requestVersion === catalogRequestVersion.current
      ) {
        setOffers(catalog.offers);
        setCatalogLoaded(true);
      }
    } catch (caught) {
      if (
        mountedRef.current &&
        requestVersion === catalogRequestVersion.current
      ) {
        setCatalogLoaded(true);
        setCatalogError(
          caught instanceof Error
            ? caught.message
            : "商品暂时无法读取，请稍后重试。",
        );
      }
    }
  };

  useEffect(() => {
    setPageMetadata(
      "Bomana 战争雷霆工具 | CheemsPay",
      HOMEPAGE_SALES_PAUSED
        ? "Bomana Enhanced：战术地图、导航、离线高程与武器解算。在线支付暂时不可用，请先使用免费版。"
        : "Bomana Enhanced：战术地图、导航、离线高程与武器解算，提供 3 天试用和 1 年授权。",
    );
    mountedRef.current = true;
    const referralFromUrl = new URLSearchParams(window.location.search).get(
      "ref",
    );
    if (referralFromUrl) {
      setReferralCode(referralFromUrl.trim().toUpperCase().slice(0, 16));
      setReferralSource("LINK");
    }
    if (HOMEPAGE_SALES_PAUSED) {
      sessionStorage.removeItem(PENDING_STOREFRONT_PRODUCT_KEY);
    } else {
      void loadCatalog();
    }
    void apiRequest<SessionResponse>("/api/session")
      .then(async () => {
        if (!mountedRef.current) {
          return;
        }
        setSignedIn(true);
        const result = await apiRequest<AccountResponse>("/api/account");
        if (mountedRef.current) {
          setAccount(result.account);
        }
      })
      .catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!signedIn) {
      return;
    }
    const returnTo = new URLSearchParams(window.location.search).get("returnTo");
    if (!returnTo) {
      return;
    }
    const returnPath = storefrontReturnPath(
      window.location.search,
      window.location.origin,
    );
    if (returnPath !== "/") {
      window.location.replace(returnPath);
    }
  }, [signedIn]);

  useEffect(() => {
    if (!signedIn || referralSource !== "LINK" || !referralCode) {
      return;
    }
    void apiRequest("/api/account/referral/claim", {
      method: "POST",
      body: JSON.stringify({ code: referralCode }),
    })
      .then(() => setReferralSource("CODE"))
      .catch(() => undefined);
  }, [referralCode, referralSource, signedIn]);

  useEffect(() => {
    if (
      HOMEPAGE_SALES_PAUSED ||
      !signedIn ||
      !catalogLoaded ||
      !account ||
      pendingPurchaseHandled.current
    ) {
      return;
    }
    const pendingProductId = takePendingStorefrontProduct(sessionStorage);
    if (!pendingProductId) {
      return;
    }
    pendingPurchaseHandled.current = true;
    buy(pendingProductId, account);
  }, [account, catalogLoaded, signedIn]);

  const selectMode = (nextMode: Mode) => {
    setMode(nextMode);
    setPasswordVisible(false);
    setTwoFactorPending(false);
    setTwoFactorMethod("totp");
    setTwoFactorCode("");
    setError("");
  };

  const revealAccess = (nextMode: Mode) => {
    if (signedIn) {
      return;
    }
    selectMode(nextMode);
    setAccessOpen(true);
  };

  const closeAccess = () => {
    setAccessOpen(false);
    setPasswordVisible(false);
    sessionStorage.removeItem(PENDING_STOREFRONT_PRODUCT_KEY);
  };

  const startPayment = async (
    productId: string,
    pendingRecoveryEmail?: string,
    referralCodeForOrder?: string,
  ) => {
    setBusyProduct(productId);
    setPurchaseError("");
    try {
      const order = await createCheemsPayOrder({
        productId,
        refundPolicyAccepted: acceptedRefundPolicies[productId] === true,
        pendingRecoveryEmail,
        referralCode: referralCodeForOrder,
      });
      sessionStorage.setItem("cheemspay-last-order", order.orderId);
      window.location.assign(order.paymentUrl);
    } catch (caught) {
      setPurchaseError(caught instanceof Error ? caught.message : "无法创建订单");
      setBusyProduct("");
    }
  };

  const buy = (
    productId: string,
    currentAccount: AccountResponse["account"] | null = account,
  ) => {
    const offer = offers.find((item) => item.productId === productId);
    if (
      offer &&
      requiresRefundPolicyConsent(offer) &&
      acceptedRefundPolicies[productId] !== true
    ) {
      setPurchaseError("请先确认该商品的退款政策");
      return;
    }
    if (!signedIn) {
      rememberPendingStorefrontProduct(productId, sessionStorage);
      revealAccess("register");
      return;
    }
    if (isFirstPurchaseAccount(currentAccount)) {
      setCheckoutProductId(productId);
      setCheckoutEmail("");
      setCheckoutReferralCode(referralCode);
      return;
    }
    void startPayment(productId, undefined, referralCode);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") {
        if (twoFactorPending) {
          await apiRequest(
            twoFactorMethod === "totp"
              ? "/api/auth/two-factor/verify-totp"
              : "/api/auth/two-factor/verify-backup-code",
            {
              method: "POST",
              body: JSON.stringify({
                code: twoFactorCode.trim(),
                trustDevice: false,
              }),
            },
          );
        } else {
          const result = await signInWithUsername(username, password);
          if (result.twoFactorRequired) {
            setPassword("");
            setPasswordVisible(false);
            setTwoFactorPending(true);
            return;
          }
        }
      } else {
        await apiRequest<RegistrationResponse>("/api/identity/register", {
          method: "POST",
          body: JSON.stringify({
            username,
            password,
            recoverySecret,
            acknowledgeAccountLossRisk: acknowledged,
            referralCode: referralCode.trim() || undefined,
            referralSource,
          }),
        });
      }
      const returnPath = storefrontReturnPath(
        window.location.search,
        window.location.origin,
      );
      if (hasPendingTeamInviteToBind(returnPath, sessionStorage)) {
        const session = await apiRequest<SessionResponse>("/api/session");
        bindPendingTeamInviteToAuthenticatedUser(returnPath, session.user.id, sessionStorage);
        window.location.assign(returnPath);
        return;
      }
      if (returnPath !== "/") {
        window.location.assign(returnPath);
        return;
      }
      const accountResult = await apiRequest<AccountResponse>("/api/account");
      setAccount(accountResult.account);
      setSignedIn(true);
      setAccessOpen(false);
      if (window.location.search) {
        window.history.replaceState({}, "", "/");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "操作失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      activeItem="war-thunder"
      action={
        signedIn ? (
          <TopbarAccountLink />
        ) : (
          <button
            aria-expanded={accessOpen}
            aria-haspopup="dialog"
            className="topbar-cta"
            onClick={() => revealAccess("login")}
            type="button"
          >
            登录
          </button>
        )
      }
    >
      <BomanaStorefront
        acceptedRefundPolicies={acceptedRefundPolicies}
        busyProduct={busyProduct}
        catalogError={catalogError}
        catalogLoaded={catalogLoaded}
        offers={offers}
        onBuy={buy}
        onRefundPolicyChange={(productId, accepted) => {
          setAcceptedRefundPolicies((current) => ({
            ...current,
            [productId]: accepted,
          }));
          setPurchaseError("");
        }}
        onRetryCatalog={() => void loadCatalog()}
        purchaseError={purchaseError}
        salesPaused={HOMEPAGE_SALES_PAUSED}
        signedIn={signedIn}
      />

      <Dialog
        className="account-access-dialog"
        closeLabel="关闭登录窗口"
        dismissible={!busy}
        onClose={closeAccess}
        open={accessOpen}
        title="账户登录与注册"
      >
        <div className="account-access">
          <div className="auth-card">
          <div aria-label="账户操作" className="mode-tabs" role="tablist">
            <button
              aria-controls="auth-access-panel"
              aria-selected={mode === "login"}
              className={mode === "login" ? "active" : ""}
              id="auth-login-tab"
              onClick={() => selectMode("login")}
              role="tab"
              type="button"
            >
              登录
            </button>
            <button
              aria-controls="auth-access-panel"
              aria-selected={mode === "register"}
              className={mode === "register" ? "active" : ""}
              id="auth-register-tab"
              onClick={() => selectMode("register")}
              role="tab"
              type="button"
            >
              注册
            </button>
          </div>

          <div className="form-heading">
            <h2>
              {mode === "login"
                ? twoFactorPending
                  ? twoFactorMethod === "totp"
                    ? "验证动态口令"
                    : "验证备用码"
                  : "欢迎回来"
                : "创建账号"}
            </h2>
            <p>
              {mode === "login"
                ? twoFactorPending
                  ? twoFactorMethod === "totp"
                    ? "打开验证器，输入当前 6 位数字"
                    : "输入保存过的一枚一次性备用码"
                  : "使用用户名和密码登录"
                : "注册不需要邮箱。恢复暗号可选。"}
            </p>
          </div>

          <form
            aria-busy={busy || undefined}
            aria-labelledby={mode === "login" ? "auth-login-tab" : "auth-register-tab"}
            id="auth-access-panel"
            onSubmit={submit}
            role="tabpanel"
          >
            {mode === "login" && twoFactorPending ? (
              <>
                <div className="two-factor-login-summary">
                  <span>正在登录</span>
                  <strong>{username}</strong>
                  <button
                    className="inline-action"
                    disabled={busy}
                    onClick={() => {
                      setTwoFactorPending(false);
                      setTwoFactorMethod("totp");
                      setTwoFactorCode("");
                      setError("");
                    }}
                    type="button"
                  >
                    换一个账号
                  </button>
                </div>
                <Field
                  label={
                    twoFactorMethod === "totp"
                      ? "6 位动态口令"
                      : "一次性备用码"
                  }
                  hint={
                    twoFactorMethod === "totp"
                      ? "约每 30 秒更新"
                      : "每枚备用码成功使用后立即失效"
                  }
                >
                  <input
                    autoComplete={
                      twoFactorMethod === "totp"
                        ? "one-time-code"
                        : "off"
                    }
                    autoFocus
                    inputMode={
                      twoFactorMethod === "totp" ? "numeric" : "text"
                    }
                    maxLength={twoFactorMethod === "totp" ? 6 : 64}
                    minLength={6}
                    onChange={(event) =>
                      setTwoFactorCode(
                        twoFactorMethod === "totp"
                          ? event.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6)
                          : event.target.value.slice(0, 64),
                      )
                    }
                    pattern={
                      twoFactorMethod === "totp"
                        ? "[0-9]{6}"
                        : undefined
                    }
                    placeholder={
                      twoFactorMethod === "totp"
                        ? "000000"
                        : "xxxxx-xxxxx"
                    }
                    required
                    value={twoFactorCode}
                  />
                </Field>
                <button
                  className="inline-action two-factor-method-toggle"
                  disabled={busy}
                  onClick={() => {
                    setTwoFactorMethod((current) =>
                      current === "totp" ? "backup" : "totp",
                    );
                    setTwoFactorCode("");
                    setError("");
                  }}
                  type="button"
                >
                  {twoFactorMethod === "totp"
                    ? "手机不在身边？改用备用码"
                    : "返回验证器动态口令"}
                </button>
              </>
            ) : (
              <>
                <Field label="用户名" hint="3–30 位字母、数字、点或下划线">
                  <input
                    autoComplete="username"
                    minLength={3}
                    maxLength={30}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="cheems.user"
                    required
                    value={username}
                  />
                </Field>
                <PasswordField
                  mode={mode}
                  onChange={(event) => setPassword(event.target.value)}
                  onRevealChange={setPasswordVisible}
                  revealed={passwordVisible}
                  value={password}
                />

                {mode === "register" ? (
                  <>
                    <Field
                      label="恢复暗号（可选）"
                      hint="如设置，至少 12 位；不要填写生日、宠物名，也不要与密码相同"
                    >
                      <input
                        autoComplete="off"
                        minLength={12}
                        maxLength={128}
                        onChange={(event) =>
                          setRecoverySecret(event.target.value)
                        }
                        placeholder="可留空；设置后可找回未付款账号"
                        type="password"
                        value={recoverySecret}
                      />
                    </Field>
                    <Field
                      label="邀请码（可选）"
                      hint={
                        referralSource === "LINK"
                          ? "已从推荐链接带入；付款前仍可修改"
                          : "朋友分享给你的 8–16 位字母数字代码"
                      }
                    >
                      <input
                        autoComplete="off"
                        maxLength={16}
                        minLength={8}
                        onChange={(event) => {
                          setReferralCode(
                            event.target.value.toUpperCase().slice(0, 16),
                          );
                          setReferralSource("CODE");
                        }}
                        placeholder="例如 ABCD2345"
                        value={referralCode}
                      />
                    </Field>
                    <label className="risk-box">
                      <input
                        checked={acknowledged}
                        onChange={(event) =>
                          setAcknowledged(event.target.checked)
                        }
                        required
                        type="checkbox"
                      />
                      <span>
                        <strong>我理解账号找回风险</strong>
                        未绑定邮箱且未设置恢复暗号时，忘记密码将无法找回普通账号。首次付款后，恢复暗号找回会停用，届时可选择绑定邮箱，或用历史付款的同一支付宝身份支付
                        0.01 元身份验证服务费找回；该费用不退款。
                      </span>
                    </label>
                  </>
                ) : null}
              </>
            )}

            {error ? (
              <AsyncStatus tone="error">
                {error}
              </AsyncStatus>
            ) : null}
            <Button
              busy={busy}
              busyLabel="请稍候…"
              type="submit"
            >
              {mode === "login"
                  ? twoFactorPending
                    ? twoFactorMethod === "totp"
                      ? "验证并登录"
                      : "使用备用码登录"
                    : "登录"
                  : "创建 CheemsPay 账号"}
            </Button>
          </form>

          {mode === "login" && !twoFactorPending ? (
            <a className="text-link auth-recover-link" href="/recover">
              忘记账密？
            </a>
          ) : null}
          {mode === "register" || twoFactorPending ? (
            <div className="cost-note">
              <span aria-hidden="true">✦</span>
              {twoFactorPending
                ? twoFactorMethod === "totp"
                  ? "动态口令在你的验证器中本地生成。"
                  : "每枚备用码只能使用一次。"
                : "注册不需要邮箱。"}
            </div>
          ) : null}
          </div>
        </div>
      </Dialog>
      <RecoveryEmailCheckoutDialog
        busy={busyProduct === checkoutProductId}
        email={checkoutEmail}
        error={purchaseError}
        onClose={() => {
          if (busyProduct !== checkoutProductId) {
            setCheckoutProductId("");
          }
        }}
        onEmailChange={setCheckoutEmail}
        onReferralChange={setCheckoutReferralCode}
        onSkip={() =>
          void startPayment(checkoutProductId, undefined, checkoutReferralCode)
        }
        onSubmit={() =>
          void startPayment(
            checkoutProductId,
            checkoutEmail,
            checkoutReferralCode,
          )
        }
        open={Boolean(checkoutProductId)}
        referralCode={checkoutReferralCode}
      />
    </Shell>
  );
}
